import React, { useEffect } from "react";
import { FiTrash2, FiAlertTriangle, FiInfo, FiCheckCircle, FiX } from "react-icons/fi";

const typeStyles = {
  danger: {
    iconBg: "bg-red-100 text-red-600 ring-4 ring-red-50",
    icon: <FiTrash2 size={24} />,
    confirmBtn: "bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white shadow-sm shadow-red-200",
  },
  warning: {
    iconBg: "bg-amber-100 text-amber-600 ring-4 ring-amber-50",
    icon: <FiAlertTriangle size={24} />,
    confirmBtn: "bg-primary hover:bg-[#1b1449] focus:ring-purple-500 text-white shadow-sm shadow-purple-200",
  },
  info: {
    iconBg: "bg-purple-100 text-purple-600 ring-4 ring-purple-50",
    icon: <FiInfo size={24} />,
    confirmBtn: "bg-primary hover:bg-[#1b1449] focus:ring-purple-500 text-white",
  },
  success: {
    iconBg: "bg-emerald-100 text-emerald-600 ring-4 ring-emerald-50",
    icon: <FiCheckCircle size={24} />,
    confirmBtn: "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500 text-white",
  },
};

const CustomConfirmModal = ({
  isOpen,
  title = "Are you sure?",
  message = "Please confirm your action to proceed.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "danger",
  onConfirm,
  onCancel,
  isLoading = false,
  showCancel = true,
}) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === "Escape") {
        onCancel && onCancel();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const currentType = typeStyles[type] || typeStyles.danger;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-200"
      onClick={onCancel}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-slate-900/10 transition-all transform scale-100 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onCancel}
          className="absolute right-4 top-4 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
          aria-label="Close modal"
        >
          <FiX size={18} />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${currentType.iconBg} mb-4`}>
            {currentType.icon}
          </div>

          <h3 className="text-xl font-bold text-slate-900 tracking-tight">
            {title}
          </h3>

          <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-xs">
            {message}
          </p>
        </div>

        <div className="mt-6 flex items-center gap-3">
          {showCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:opacity-50"
            >
              {cancelText}
            </button>
          )}

          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`w-full rounded-xl py-2.5 px-4 text-sm font-semibold transition focus:outline-none focus:ring-2 disabled:opacity-60 flex items-center justify-center gap-2 ${currentType.confirmBtn}`}
          >
            {isLoading && (
              <svg className="h-4 w-4 animate-spin text-current" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomConfirmModal;
